import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { title: "", body: "", start_date: "", end_date: "", link_url: "", link_text: "", active: true };

const todayLive = (a) => {
  if (!a.active) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (a.start_date && today < a.start_date) return false;
  if (a.end_date && today > a.end_date) return false;
  return true;
};

const AdminAnnouncements = () => {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/announcements").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const inputCls = "bg-white border-[var(--ir-border)] rounded-none focus-visible:ring-[var(--ir-blue)]";
  const outlineBtn = "border-[var(--ir-border)] bg-white text-[var(--ir-blue)] hover:bg-[var(--ir-surface2)] rounded-none h-8";
  const dangerBtn = "border-rose-300 bg-white text-rose-600 hover:bg-rose-50 rounded-none h-8";

  const openAdd = () => { setForm(emptyForm); setDialog({ open: true, editing: null }); };
  const openEdit = (a) => {
    setForm({ title: a.title || "", body: a.body || "", start_date: a.start_date || "", end_date: a.end_date || "", link_url: a.link_url || "", link_text: a.link_text || "", active: a.active });
    setDialog({ open: true, editing: a });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = {
      title: form.title, body: form.body,
      start_date: form.start_date || null, end_date: form.end_date || null,
      link_url: form.link_url || null, link_text: form.link_text || null, active: form.active,
    };
    try {
      if (dialog.editing) { await api.patch(`/admin/announcements/${dialog.editing.id}`, payload); toast.success("Announcement updated"); }
      else { await api.post("/admin/announcements", payload); toast.success("Announcement created"); }
      setDialog({ open: false, editing: null }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (a) => {
    try { await api.patch(`/admin/announcements/${a.id}`, { active: !a.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try { await api.delete(`/admin/announcements/${a.id}`); toast.success("Announcement deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-announcements">
      <div className="flex justify-between items-center mb-4">
        <p className="text-[var(--ir-muted)] text-sm">Banners shown at the top of the affiliate dashboard. Only active announcements within their date range display; the most recent shows if several are live.</p>
        <Button onClick={openAdd} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none" data-testid="add-announcement-btn"><Plus className="w-4 h-4 mr-2" />New Announcement</Button>
      </div>
      <div className="bg-white border border-[var(--ir-border)] rounded-lg overflow-x-auto soft-shadow">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--ir-border)] hover:bg-transparent">
              <TableHead className="text-[var(--ir-muted)]">Announcement</TableHead>
              <TableHead className="text-[var(--ir-muted)]">Window</TableHead>
              <TableHead className="text-[var(--ir-muted)] text-center">Live now</TableHead>
              <TableHead className="text-[var(--ir-muted)] text-center">Active</TableHead>
              <TableHead className="text-[var(--ir-muted)] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow className="border-[var(--ir-border)]"><TableCell colSpan={5} className="text-center text-[var(--ir-muted)] py-10">No announcements yet.</TableCell></TableRow>}
            {items.map((a) => (
              <TableRow key={a.id} className="border-[var(--ir-border)]" data-testid={`announcement-row-${a.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2 text-[var(--ir-text)] text-sm font-medium"><Megaphone className="w-4 h-4 text-[var(--ir-orange)]" />{a.title}</div>
                  {a.body && <div className="text-[var(--ir-muted)] text-xs max-w-[320px] truncate mt-0.5">{a.body}</div>}
                </TableCell>
                <TableCell className="text-[var(--ir-muted)] text-xs">{a.start_date || "—"} → {a.end_date || "—"}</TableCell>
                <TableCell className="text-center">{todayLive(a) ? <Badge className="rounded-none border bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[10px]">Live</Badge> : <span className="text-[var(--ir-muted)] text-xs">—</span>}</TableCell>
                <TableCell className="text-center"><Switch checked={a.active} onCheckedChange={() => toggleActive(a)} data-testid={`toggle-announcement-${a.id}`} /></TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => openEdit(a)} className={outlineBtn} data-testid={`edit-announcement-${a.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(a)} className={dangerBtn} data-testid={`delete-announcement-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="bg-white border-[var(--ir-border)] text-[var(--ir-text)] rounded-none max-w-lg" data-testid="announcement-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{dialog.editing ? "Edit Announcement" : "New Announcement"}</DialogTitle>
          <DialogDescription className="text-[var(--ir-muted)] text-xs">Shown as a banner on the affiliate dashboard while active and within its date range.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="announcement-title-input" /></div>
            <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Message</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={3} className={inputCls} data-testid="announcement-body-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} data-testid="announcement-start-input" /></div>
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} data-testid="announcement-end-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Button link (optional)</Label><Input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="https://…" className={inputCls} data-testid="announcement-linkurl-input" /></div>
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Button text (optional)</Label><Input value={form.link_text} onChange={(e) => setForm({ ...form, link_text: e.target.value })} placeholder="Learn more" className={inputCls} data-testid="announcement-linktext-input" /></div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="announcement-active-switch" />
              <span className="text-sm text-[var(--ir-text)]">Active</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, editing: null })} className="border-[var(--ir-border)] bg-white text-[var(--ir-text)] rounded-none">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none disabled:opacity-50" data-testid="save-announcement-btn">{saving ? "Saving…" : (dialog.editing ? "Save changes" : "Create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAnnouncements;
